/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { TIME_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { Theme } from '@coursera/cds-core';
import { Link } from '@coursera/cds-core';
import { VideoIcon } from '@coursera/cds-icons';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import type { UseCalendarEvents_LiveEventFragment as LiveEvent } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCalendarEvents';

import _t from 'i18n!nls/degree-home';

import { CalendarListItem } from './CalendarListItem';
import { LivePill } from './Pill';
import { filterOptionValues } from './constants/constants';

const styles = {
  courseName: css`
    a {
      color: inherit !important;
    }
  `,
  dotSeparator: (theme: Theme) => css`
    margin: ${theme.spacing(0, 8)};
  `,
};

const dotSeparator = <span css={styles.dotSeparator}>•</span>;

export const LiveEventItem: React.FunctionComponent<{ item: LiveEvent }> = ({ item }) => {
  const { filter } = useDegreeContext();
  const currentTime = Date.now();
  // start and end time types are undefined because of the autogenerated GRPC timestamp
  // however, in practice there are no live events without a start/end time
  const hasStarted = item.startTime && currentTime >= new Date(item.startTime).getTime();
  const hasEnded = item.endTime && currentTime > new Date(item.endTime).getTime();
  const isLive = hasStarted && !hasEnded;
  const startTime = formatDateTimeDisplay(item.startTime || '', TIME_ONLY_DISPLAY);
  const subHeading = (
    <>
      <span css={styles.courseName}>
        <Link
          href={item.courseUrl}
          variant="quiet"
          data-e2e={`calendar-list-live-event-item-course-name-link ${item.course.name}`}
        >
          {item.course.name}
        </Link>
      </span>
      {dotSeparator}
      {hasStarted ? _t('Started at #{startTime}', { startTime }) : _t('Starts at #{startTime}', { startTime })}
    </>
  );

  const eventDuration =
    item.startTime && item.endTime ? new Date(item.endTime).getTime() - new Date(item.startTime).getTime() : undefined;
  return (
    <CalendarListItem
      itemIcon={<VideoIcon size="large" />}
      itemTypeName={_t('Live Event')}
      itemName={item.title}
      itemUrl={!hasEnded ? item.eventUrl : undefined}
      duration={eventDuration}
      statusPill={isLive ? <LivePill /> : null}
      subHeading={subHeading}
      trackingName="degree_home_live_event_link"
      courseId={item.course.id}
      itemId={item.id}
      isDismissible={filter === filterOptionValues.All && !item.isDismissed}
      isRestorable={filter === filterOptionValues.LiveEvents && item.isDismissed}
      itemFilterType={filterOptionValues.LiveEvents}
    />
  );
};
